/**
 * AvatarGroup component displays a group of user avatars with an optional maximum number of visible avatars.
 * Note that it can be used to display one single avatar as well.
 * If the number of avatars exceeds the maximum, the overflow avatars are handled by the AvatarOverflow component.
 *
 * @remarks
 * - The maximum number of visible avatars is capped by a strict limit (`MAX_AVATARS_LIMIT`).
 * - If no avatars are provided, the component renders nothing.
 * - Figma Ref : https://www.figma.com/design/ChbJKqDEPsJLwmGNHtbOAF/Design-System-v.2.0?node-id=2528-9820&p=f&m=dev
 * @todo
 * - is not exactly styled as the figma
 *   --> we must create a task to update it according to the design system across the app.
 * - does not yet support images or icons as avatars, only initials.
 *
 * @param props - The props for the AvatarGroup component.
 * @param props.avatars - An array of Avatar objects to display.
 * @param props.options - Optional configuration for the group.
 * @param props.options.maxVisible - The maximum number of avatars to display before overflowing.
 *
 * @returns A React element displaying the avatar group, or an empty fragment if no avatars are provided.
 *
 * @example
 * ```tsx
 * <AvatarGroup avatars={userAvatars} options={{ maxVisible: 5 }} />
 * ```
 */

import React from 'react';
import Avatar from './types/avatar';
import AvatarList from './components/AvatarList';
import AvatarOverflow from './components/AvatarOverflow';

interface AvatarGroupProps {
  avatars: Avatar[];
  options?: {
    maxVisible?: number;
  };
}

const MAX_AVATARS_LIMIT = 10;

const AvatarGroup: React.FC<AvatarGroupProps> = ({ avatars, options }) => {
  if (avatars.length === 0) return null;

  const isMaxVisibleOptionIsValid = options?.maxVisible && options.maxVisible > 0 && options.maxVisible < MAX_AVATARS_LIMIT;
  const maxVisible = isMaxVisibleOptionIsValid ? options.maxVisible : MAX_AVATARS_LIMIT;
  const visibleAvatars = avatars.slice(0, maxVisible).reverse();
  const overflowAvatars = avatars.slice(maxVisible);

  return (
    <div className="avatar-group" role="group" aria-label={`${avatars.length} avatars`}>
      {overflowAvatars.length > 0 && <AvatarOverflow avatars={overflowAvatars} />}
      <AvatarList avatars={visibleAvatars} />
    </div>
  );
};

export default AvatarGroup;
